<template>
  <div v-if="!isLoggedIn" class="min-h-screen bg-[#F8FAFC] flex flex-col items-center justify-center p-4">
    <div class="w-full max-w-md bg-white border border-slate-200 rounded-3xl p-8 shadow-xl">
      <div class="text-center mb-8">
        <div class="inline-flex items-center justify-center w-16 h-16 bg-[#F2C94C] rounded-2xl mb-4">
          <IconShield class="w-8 h-8 text-white" />
        </div>
        <h1 class="text-2xl font-bold text-slate-900">对一对身份</h1>
      </div>
      <form @submit.prevent="handleLogin" class="space-y-6">
        <div>
          <input
            v-model="adminId"
            type="text"
            placeholder="管理 ID"
            class="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-slate-900 focus:border-[#F2C94C] outline-none"
            required
          />
        </div>
        <div>
          <input
            v-model="passKey"
            type="password"
            placeholder="通行密钥"
            class="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-slate-900 focus:border-[#F2C94C] outline-none"
            required
          />
        </div>
        <p v-if="loginError" class="text-red-500 text-sm text-center">{{ loginError }}</p>
        <button type="submit" class="w-full bg-[#F2C94C] text-white font-bold py-4 rounded-xl hover:bg-[#F2C94C]/90 transition-all shadow-lg shadow-[#F2C94C]/20">准许进入</button>
      </form>
    </div>
  </div>

  <div v-else class="flex h-screen overflow-hidden bg-[#F8FAFC] text-[#1A1C1E] font-sans">
    <!-- Mobile Overlay -->
    <div
      v-if="isSidebarOpen"
      class="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 md:hidden"
      @click="isSidebarOpen = false"
    />

    <Sidebar
      :active-view="view"
      :context="context"
      :user-stats="userStats"
      :is-open="isSidebarOpen"
      :is-collapsed="isSidebarCollapsed"
      @navigate="handleSetView"
      @logout="handleLogout"
      @update:isOpen="isSidebarOpen = $event"
      @update:isCollapsed="isSidebarCollapsed = $event"
      @update:isBriefingMode="setIsBriefingMode($event)"
    />

    <main class="flex-1 flex flex-col relative h-full overflow-hidden" id="main-scroll-container">
      <Header
        :view="view"
        :selected-topic="selectedTopic"
        :context="context"
        @toggle-sidebar="toggleSidebar"
        @set-view="handleSetView"
        @set-selected-topic="setSelectedTopic($event)"
      />

      <div class="flex-1 overflow-y-auto pb-32">
        <div class="max-w-7xl mx-auto p-4 md:p-6 lg:p-8">
          <router-view
            :key="$route.fullPath"
            @search="handleSearch"
            @topic-click="handleTopicClick"
            @history-navigate="handleHistoryNavigate"
            @update-history="handleUpdateHistory"
            @diagnosis-complete="handleDiagnosisComplete"
            @expert-click="handleExpertClick"
            @book-consultant="handleBookConsultant"
            @view-case="handleViewCase"
            @delete-study-record="handleDeleteStudyRecord"
            @delete-practice-record="handleDeletePracticeRecord"
            @delete-chat-record="handleDeleteChatRecord"
            @clear-study-records="handleClearStudyRecords"
            @clear-practice-records="handleClearPracticeRecords"
            @clear-chat-records="handleClearChatRecords"
            @reload-chat="handleReloadChat"
          />
        </div>
      </div>

      <!-- Global Bottom Input Bar -->
      <div v-if="isBriefingMode" class="absolute bottom-0 left-0 right-0 z-20 p-6 pointer-events-none">
        <div class="max-w-4xl mx-auto pointer-events-auto">
          <div class="bg-white/80 backdrop-blur-xl rounded-2xl border border-slate-200 p-2 shadow-2xl">
            <IntentionCapture
              mode="new-search"
              @search="handleSearch"
              @start-diagnose="$router.push('/diagnose-start')"
            />
          </div>
        </div>
      </div>

      <!-- Footer -->
      <footer v-if="$route.path !== '/diagnose-result'" class="py-8 text-center mt-auto">
        <p class="text-[10px] text-slate-400">
          及时、精准、有效解决管理痛点，助力每一位管理者提升管理能力
        </p>
      </footer>
    </main>
  </div>
</template>

<script>
import { mapState, mapMutations, mapActions } from 'vuex'
import { TOPICS, EXPERT_CASES } from './data'
import { generateManagementFeedback } from './services/ai-service'
import { backupUserData, checkCloudBackup, restoreFromBackup, shouldBackup, getLastBackupTime } from './services/backup'
import { verifyPassword } from './utils/auth'
import { ADMIN_CREDENTIALS } from './data/adminConfig'
import Sidebar from './components/common/Sidebar.vue'
import Header from './components/common/Header.vue'
import IntentionCapture from './components/practice/IntentionCapture.vue'
import { IconShield } from './components/icons'

export default {
  name: 'AppLayout',
  components: {
    Sidebar,
    Header,
    IntentionCapture,
    IconShield
  },
  data() {
    return {
      isSidebarOpen: false,
      isBackupRestored: false,
      isSidebarCollapsed: false,
      selectedExpert: null,
      adminId: '',
      passKey: '',
      loginError: ''
    }
  },
  computed: {
    ...mapState([
      'isLoggedIn', 'view', 'isBriefingMode', 'context', 'userStats',
      'history', 'studyRecords', 'practiceRecords', 'experts',
      'targetTopicId', 'pendingQuery', 'selectedTopic', 'diagnosticContext',
      'aiFeedback', 'activePrescription', 'activeHistoryId',
      'isGeneratingFeedback', 'selectedScenario'
    ])
  },
  mounted() {
    window.addEventListener('beforeunload', this.handleBeforeUnload)
    this.restoreBackup()
  },
  beforeDestroy() {
    window.removeEventListener('beforeunload', this.handleBeforeUnload)
  },
  methods: {
    ...mapMutations([
      'SET_IS_LOGGED_IN', 'SET_VIEW', 'SET_IS_BRIEFING_MODE', 'SET_CONTEXT',
      'SET_USER_STATS', 'SET_HISTORY', 'SET_STUDY_RECORDS', 'SET_PRACTICE_RECORDS',
      'SET_EXPERTS', 'SET_TARGET_TOPIC_ID', 'SET_PENDING_QUERY',
      'SET_SELECTED_TOPIC', 'SET_DIAGNOSTIC_CONTEXT', 'SET_AI_FEEDBACK',
      'SET_ACTIVE_PRESCRIPTION', 'SET_ACTIVE_HISTORY_ID',
      'SET_IS_GENERATING_FEEDBACK', 'SET_SELECTED_SCENARIO'
    ]),
    ...mapActions([
      'recordStudyAction', 'recordPracticeAction', 'handleTrackInteraction'
    ]),

    handleBeforeUnload() {
      backupUserData()
    },

    async restoreBackup() {
      if (!this.isLoggedIn || this.isBackupRestored) return
      try {
        const lastBackupTime = getLastBackupTime()
        const oneDayMs = 24 * 60 * 60 * 1000
        const shouldCheckCloud = !lastBackupTime || (Date.now() - lastBackupTime > oneDayMs)
        if (shouldCheckCloud) {
          const cloudBackup = await checkCloudBackup()
          if (cloudBackup) {
            const localTimestamp = lastBackupTime || 0
            const cloudTimestamp = cloudBackup.timestamp
            if (cloudTimestamp > localTimestamp) {
              restoreFromBackup(cloudBackup)
              window.location.reload()
              return
            }
          }
        }
        if (shouldBackup()) {
          backupUserData()
        }
      } catch (error) {
        console.error('[Backup] Restore check failed:', error)
      }
      this.isBackupRestored = true
    },

    checkAuthAndExecute(action) {
      if (!this.isLoggedIn) {
        alert('指挥官，请先对一对身份（登录）以解锁高级战术分析。')
        return
      }
      action()
    },

    handleLogin(e) {
      e.preventDefault()
      this.loginError = ''

      // 验证管理 ID
      if (this.adminId.trim() !== ADMIN_CREDENTIALS.adminId) {
        this.loginError = '管理 ID 或通行密钥错误'
        return
      }

      // 验证密码（SHA256 + 盐）
      const isValid = verifyPassword(this.passKey, ADMIN_CREDENTIALS.hash, ADMIN_CREDENTIALS.salt)
      if (!isValid) {
        this.loginError = '管理 ID 或通行密钥错误'
        return
      }

      this.SET_IS_LOGGED_IN(true)
      this.SET_VIEW('home')
      this.adminId = ''
      this.passKey = ''
    },

    handleSearch(query, customDiagnosticContext) {
      this.SET_ACTIVE_PRESCRIPTION(null)
      this.SET_PENDING_QUERY(query)

      const topic = this.selectedTopic || {
        id: 'custom',
        title: query,
        type: '战友最痛'
      }
      this.SET_SELECTED_TOPIC(topic)
      this.$router.push(`/topic/${topic.id}`).catch(() => {})

      if (customDiagnosticContext) {
        this.SET_DIAGNOSTIC_CONTEXT(customDiagnosticContext)
        this.SET_IS_GENERATING_FEEDBACK(true)
        generateManagementFeedback(query, customDiagnosticContext.teamContext, customDiagnosticContext, [])
          .then(feedback => {
            const newPrescription = {
              truth: feedback,
              script: { opening: '“关于您的追问...”', responses: ['正在思考...'], closing: '“希望这能帮到您。”' },
              redLines: []
            }
            this.SET_ACTIVE_PRESCRIPTION(newPrescription)
            if (this.activeHistoryId) {
              const newUserMsg = { id: Date.now().toString(), role: 'user', content: query }
              const newAiMsg = { id: (Date.now() + 1).toString(), role: 'ai', content: feedback }
              const updated = this.history.map(item =>
                item.id === this.activeHistoryId
                  ? { ...item, chatHistory: [...(item.chatHistory || []), newUserMsg, newAiMsg] }
                  : item
              )
              this.SET_HISTORY(updated)
            }
          })
          .catch(error => {
            console.error('Follow-up error:', error)
          })
          .finally(() => {
            this.SET_IS_GENERATING_FEEDBACK(false)
          })
      }
    },

    handleTopicClick(topic) {
      this.SET_ACTIVE_PRESCRIPTION(null)
      this.SET_SELECTED_TOPIC(topic)
      this.SET_PENDING_QUERY(topic.title)
      this.$router.push(`/topic/${topic.id}`).catch(() => {})
    },

    handleHistoryNavigate(targetView, item) {
      this.SET_CONTEXT(item.context)
      this.SET_ACTIVE_HISTORY_ID(item.id)
      if (item.diagnosticContext) {
        this.SET_DIAGNOSTIC_CONTEXT(item.diagnosticContext)
      }
      if (item.topicId) {
        const topic = TOPICS.find(t => t.id === item.topicId)
        if (topic) this.SET_SELECTED_TOPIC(topic)
      } else {
        this.SET_SELECTED_TOPIC({ id: 'custom', title: item.query, type: '战友最痛' })
        this.SET_AI_FEEDBACK(item.aiResponse)
      }
      this.SET_ACTIVE_PRESCRIPTION(item.prescription || null)
      this.SET_VIEW(targetView)
    },

    handleUpdateHistory(chatHistory) {
      if (!this.activeHistoryId) return
      const updated = this.history.map(item =>
        item.id === this.activeHistoryId ? { ...item, chatHistory } : item
      )
      this.SET_HISTORY(updated)
    },

    handleDiagnosisComplete(diagnostic) {
      this.SET_DIAGNOSTIC_CONTEXT(diagnostic)
      this.SET_CONTEXT(diagnostic.teamContext)

      const topic = this.selectedTopic || {
        id: 'diagnostic-result',
        title: `针对【${this.pendingQuery}】的研判报告`,
        type: '战友最痛'
      }
      this.SET_SELECTED_TOPIC(topic)

      const prescription = {
        truth: `### 研判真相：${this.pendingQuery}\n\n**研判维度：** ${diagnostic.questionSet === 'talent' ? '人才保留' : diagnostic.questionSet === 'execution' ? '执行力穿透' : '基础组织画像'}\n\n**核心发现：** ${Object.values(diagnostic).filter(v => typeof v === 'string' && v.length > 0).join(' | ')}\n\n--- \n\n基于您的团队画像，AI 管理能力提升助手建议：\n\n1. **精准打击**：针对研判出的核心问题，立即启动专项对齐。\n2. **工具赋能**：引入匹配当前阶段的管理工具。`,
        summary: `当前团队核心骨干流失风险已达临界点，主要源于业务快速扩张期压力传导失衡，以及管理者对核心人才情绪价值与成长路径规划的长期忽视。建议指挥官立即开启非业务导向的一对一深度面谈，剥离KPI考核，纯粹探寻其个人职业发展诉求与当前核心痛点，切忌单纯依靠物质承诺进行防御性挽留。通过此次精准的心理干预与资源倾斜，预期能有效缓解骨干成员的职业倦怠感，重建团队信任纽带，将核心人才流失风险降低至安全水位，从而确保组织在高速行军中的核心战斗力与业务连续性。`,
        script: { opening: '“我们来聊聊这件事...”', responses: ['正在生成话术...'], closing: '“按此执行即可。”' },
        redLines: ['正在划定红线...']
      }
      this.SET_ACTIVE_PRESCRIPTION(prescription)

      const newHistoryId = Date.now().toString()
      this.SET_ACTIVE_HISTORY_ID(newHistoryId)
      const historyItem = {
        id: newHistoryId,
        query: `针对【${this.pendingQuery}】的研判报告`,
        aiResponse: '研判报告已生成',
        timestamp: Date.now(),
        context: { ...diagnostic.teamContext },
        isDeepDiagnosis: true,
        diagnosticContext: diagnostic,
        prescription
      }
      this.SET_HISTORY([historyItem, ...this.history])

      this.$router.push('/diagnose-result').catch(() => {})
      this.SET_VIEW('diagnose-start')
      this.SET_TARGET_TOPIC_ID(null)
    },

    handleExpertClick(expert) {
      this.checkAuthAndExecute(() => {
        this.selectedExpert = expert
        this.SET_VIEW('expert-profile')
        this.$router.push(`/expert/${expert.id}`).catch(() => {})
      })
    },

    handleBookConsultant(/* expertId */) {
      if (this.userStats.points < 300) {
        alert('战术积分不足，请通过实战推演获取积分。')
        return
      }
      this.SET_USER_STATS({ ...this.userStats, points: this.userStats.points - 300 })
      alert('预约申请已提交！专家将在24小时内联系您。')
    },

    handleLogout() {
      this.SET_IS_LOGGED_IN(false)
      this.SET_VIEW('home')
      this.SET_SELECTED_TOPIC(null)
      this.SET_TARGET_TOPIC_ID(null)
      this.SET_HISTORY([])
      this.SET_AI_FEEDBACK('')
      this.SET_IS_BRIEFING_MODE(false)
    },

    handleSetView(newView) {
      if (newView !== 'home' && !this.isLoggedIn) {
        alert('指挥官，请先对一对身份（登录）以解锁该模块。')
        return
      }
      this.SET_VIEW(newView)

      const viewToPath = {
        'home': '/',
        'practice': '/practice',
        'diagnose-start': '/diagnose-start',
        'diagnose-engine': '/diagnose-start',
        'history': '/history',
        'expert-profile': '/expert-profile',
        'topic-detail': this.selectedTopic ? `/topic/${this.selectedTopic.id}` : '/'
      }

      const targetPath = viewToPath[newView]
      if (targetPath && this.$route.path !== targetPath) {
        this.$router.push(targetPath).catch(() => {})
      }

      if (newView !== 'home') {
        this.SET_IS_BRIEFING_MODE(false)
      }
      if (newView !== 'practice' && newView !== 'diagnose-engine') {
        this.SET_TARGET_TOPIC_ID(null)
      }
    },

    handleViewCase(caseId, expertId) {
      const expertCase = EXPERT_CASES[caseId]
      const eId = expertId || this.selectedExpert?.id
      if (expertCase && eId) {
        this.$router.push(`/expert/${eId}/case/${caseId}`).catch(() => {})
      }
    },

    toggleSidebar() {
      if (window.innerWidth >= 768) {
        this.isSidebarCollapsed = !this.isSidebarCollapsed
      } else {
        this.isSidebarOpen = !this.isSidebarOpen
      }
    },

    // HistoryView handlers
    handleDeleteStudyRecord(id) {
      this.SET_STUDY_RECORDS(this.studyRecords.filter(r => r.id !== id))
    },
    handleDeletePracticeRecord(id) {
      this.SET_PRACTICE_RECORDS(this.practiceRecords.filter(r => r.id !== id))
    },
    handleDeleteChatRecord(id) {
      this.SET_HISTORY(this.history.filter(h => h.id !== id))
    },
    handleClearStudyRecords() {
      this.SET_STUDY_RECORDS([])
    },
    handleClearPracticeRecords() {
      this.SET_PRACTICE_RECORDS([])
    },
    handleClearChatRecords() {
      this.SET_HISTORY([])
    },
    handleReloadChat(ctx) {
      this.SET_CONTEXT(ctx)
      this.$router.push('/diagnose-start').catch(() => {})
    }
  }
}
</script>
